import React, { useCallback, useMemo, useState, useEffect, useRef, memo } from 'react';
import { connect } from 'react-redux';
import { List } from 'immutable';
import moment from 'moment';

import Popups from '../../components/Popup';
import { unwrap } from '../../utils/ChangeSpy';
import AutoCompleteContainer from '../AutoComplete';
import config from '../../config';
import { findAnalysts, findDirectors, handleUpdateCompany } from '../../actions/company/companyTarget';
import { activateTargetRole } from '../../actions/company/targetCreate';
import { usePopupContext } from '../../contexts/PopupContext';
import { useRedirectForceContext } from '../../contexts/RedirectForceContext';

const ActiveTargetPopupContainer = ({
  children,
  popup,
  formData,
  suggestions,
  buyerName,
  user,
  findDirectors,
  findAnalysts,
  handleUpdateCompany,
  activateTargetRole,
}) => {
  const { closePopup, updatePopupFormData } = usePopupContext();
  const redirectForce = useRedirectForceContext();

  const [message, setMessage] = useState('');
  const [processing, setProcessing] = useState(false);
  const formRef = useRef(formData.toJS());

  useEffect(() => {
    if (user) {
      const suggestDirectorId = user.get('moduleId');
      const suggestDirectorValue = user.get('module', '');
      let suggestAnalystId;
      let suggestAnalystValue;

      const roles = user.get('roles', List());

      if (roles.size && roles.getIn([0, 'slug']) === config.ANALYST_ASSOCIATE) {
        suggestAnalystValue = user.get('userName');
        suggestAnalystId = user.get('id');
      }

      updatePopupFormData({
        suggestDirectorId,
        suggestDirectorValue,
        suggestAnalystId,
        suggestAnalystValue,
      });
    }
  }, []);

  const handleClose = useCallback(() => {
    closePopup();
  }, [closePopup]);

  const handleSave = useCallback(() => {
    const companyId = popup.getIn(['props', 'companyId'], 0);

    activateTargetRole({
      body: {
        recordSubOwnerId: formData.get('suggestAnalystId'),
        recordOwnerId: formData.get('suggestDirectorId'),
        lastResearcherId: formData.get('suggestAnalystId'),
        lastResearchedDate: moment().format('YYYY-MM-DD'),
      },
      companyId,
      afterSuccess: () => {
        redirectForce(window.location);
      },
      afterError: () => {
        setMessage('Active target role failed. Please try again');
        setProcessing(false);
      },
    });
  }, [popup, formData, activateTargetRole, redirectForce]);

  const handleCreate = useCallback(() => {
    const companyId = popup.getIn(['props', 'companyId'], 0);

    if (companyId > 0 && !processing) {
      if (formData.get('suggestDirectorValue')) {
        setMessage('');
        setProcessing(true);
        handleSave();
      } else {
        setMessage('You must select a module');
        setProcessing(false);
      }
    }
  }, [popup, processing, formData, handleSave]);

  const handleSuggestChange = useCallback(
    ({ name, value }) => {
      if (name === 'suggests') {
        handleUpdateCompany({ name, value });
      } else {
        formRef.current[name] = value;
        updatePopupFormData({
          ...formRef.current,
          [name]: value,
        });
      }
    },
    [handleUpdateCompany, updatePopupFormData],
  );

  const getSuggest = useCallback(
    name => {
      const find = name === 'suggestDirector' ? findDirectors : findAnalysts;

      return (
        <AutoCompleteContainer
          idName={`${name}Id`}
          idValue="idValue"
          onChange={handleSuggestChange}
          onFind={find}
          suggestions={suggestions}
          suggestionsName="suggests"
          value={formData.get(`${name}Value`)}
          valueName={`${name}Value`}
        />
      );
    },
    [formData, suggestions, findDirectors, findAnalysts, handleSuggestChange],
  );

  const suggestDirector = useMemo(() => getSuggest('suggestDirector'), [getSuggest]);
  const suggestAnalyst = useMemo(() => getSuggest('suggestAnalyst'), [getSuggest]);

  const {
    props: { isFinancialBuyer = false },
  } = popup.toJS();

  return (
    <>
      <Popups.ActiveTargetPopup
        buyerName={buyerName}
        isFinancialBuyer={isFinancialBuyer}
        message={message}
        onClose={handleClose}
        onCreate={handleCreate}
        processing={processing}
        suggestAnalyst={suggestAnalyst}
        suggestDirector={suggestDirector}
      />
      {children}
    </>
  );
};

const mapStateToProps = (state, props) => ({
  ...props,
  formData: props.popup.get('formData'),
  suggestions: state.targetCompany.target.get('suggests'),
  buyerName: unwrap(state.targetCompany.info.getIn(['info', 'legalName'])),
  user: state.auth.get('user', null),
});

export default connect(mapStateToProps, {
  findAnalysts,
  findDirectors,
  handleUpdateCompany,
  activateTargetRole,
})(memo(ActiveTargetPopupContainer));
